import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Loader2, LogOut, Trash2, Inbox, Mail, MailCheck, X } from "lucide-react";
import { Seo } from "../components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STATUSES = [
  { value: "new", label: "New" },
  { value: "contacted", label: "Contacted" },
  { value: "interview", label: "Interview" },
  { value: "hired", label: "Hired" },
  { value: "not-a-fit", label: "Not a fit" },
];

const statusColor = (s) =>
  ({
    new: "text-amber-400 border-amber-400/30 bg-amber-400/10",
    contacted: "text-sky-400 border-sky-400/30 bg-sky-400/10",
    interview: "text-violet-400 border-violet-400/30 bg-violet-400/10",
    hired: "text-emerald-400 border-emerald-400/30 bg-emerald-400/10",
    "not-a-fit": "text-gray-500 border-gray-500/30 bg-gray-500/10",
  }[s] || "text-gray-400 border-white/15 bg-white/5");

const formatDetail = (detail) => {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
  return "Something went wrong.";
};

export default function AdminApplications() {
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [replyTarget, setReplyTarget] = useState(null);
  const [replyText, setReplyText] = useState("");
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();
  const token = localStorage.getItem("maze_admin_token");
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) {
      navigate("/admin/login");
      return;
    }
    axios
      .get(`${API}/admin/applications`, { headers })
      .then((res) => setApps(res.data))
      .catch((err) => {
        if (err.response?.status === 401) {
          localStorage.removeItem("maze_admin_token");
          navigate("/admin/login");
        }
      })
      .finally(() => setLoading(false));
  }, []);

  const setStatus = async (id, status) => {
    try {
      await axios.patch(`${API}/admin/applications/${id}`, { status }, { headers });
      setApps((prev) => prev.map((a) => (a.id === id ? { ...a, status } : a)));
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this application permanently?")) return;
    try {
      await axios.delete(`${API}/admin/applications/${id}`, { headers });
      setApps((prev) => prev.filter((a) => a.id !== id));
    } catch (err) {
      if (err.response?.status === 401) navigate("/admin/login");
    }
  };

  const sendReply = async () => {
    if (!replyText.trim()) return;
    setSending(true);
    try {
      await axios.post(`${API}/admin/applications/${replyTarget.id}/reply`, { message: replyText }, { headers });
      toast.success(`Reply sent to ${replyTarget.email}`);
      setApps((prev) =>
        prev.map((a) => (a.id === replyTarget.id ? { ...a, status: "contacted", replied_at: new Date().toISOString() } : a))
      );
      setReplyTarget(null);
      setReplyText("");
    } catch (err) {
      toast.error(formatDetail(err.response?.data?.detail) || "Reply failed to send.");
    } finally {
      setSending(false);
    }
  };

  const logout = () => {
    localStorage.removeItem("maze_admin_token");
    navigate("/admin/login");
  };

  const visible = filter === "all" ? apps : apps.filter((a) => (a.status || "new") === filter);

  return (
    <>
      <Seo title="Applications Inbox | Maze Security Admin" noindex />
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12" data-testid="admin-applications-page">
        <div className="flex flex-wrap items-center justify-between gap-4 mb-8">
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.25em] text-[#FF5500] mb-2">Admin</p>
            <h1 className="font-display text-3xl sm:text-4xl font-bold uppercase tracking-tight text-white flex items-center gap-3">
              <Inbox className="h-8 w-8 text-[#FF5500]" /> Applications Inbox
            </h1>
            <p className="text-sm text-gray-400 mt-1" data-testid="applications-count">
              {apps.length} application{apps.length === 1 ? "" : "s"} total
            </p>
          </div>
          <button
            onClick={logout}
            data-testid="admin-logout-button"
            className="inline-flex items-center gap-2 bg-white/5 hover:bg-white/10 border border-white/15 text-gray-200 text-sm font-medium px-4 py-2 rounded-sm transition-colors"
          >
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>

        <div className="flex flex-wrap gap-2 mb-6" data-testid="status-filters">
          {[{ value: "all", label: "All" }, ...STATUSES].map((s) => (
            <button
              key={s.value}
              onClick={() => setFilter(s.value)}
              data-testid={`filter-${s.value}`}
              className={`text-xs font-mono uppercase tracking-wider px-3 py-1.5 rounded-sm border transition-colors ${
                filter === s.value ? "border-[#FF5500] text-[#FF5500] bg-[#FF5500]/10" : "border-white/15 text-gray-400 hover:text-white"
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-gray-400 py-16 justify-center" data-testid="applications-loading">
            <Loader2 className="h-5 w-5 animate-spin" /> Loading applications…
          </div>
        ) : visible.length === 0 ? (
          <div className="border border-white/10 bg-[#181C28] rounded-sm p-12 text-center text-gray-400 text-sm" data-testid="applications-empty">
            No applications {filter !== "all" ? `with status "${filter}"` : "yet"}. New submissions from the Careers page appear here instantly.
          </div>
        ) : (
          <div className="overflow-x-auto border border-white/10 rounded-sm" data-testid="applications-table">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-[#131620] text-left text-xs font-mono uppercase tracking-wider text-gray-400">
                  <th className="px-4 py-3">Date</th>
                  <th className="px-4 py-3">Name</th>
                  <th className="px-4 py-3">Role</th>
                  <th className="px-4 py-3">Location</th>
                  <th className="px-4 py-3">Contact</th>
                  <th className="px-4 py-3">Licence</th>
                  <th className="px-4 py-3">Notes</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3"></th>
                </tr>
              </thead>
              <tbody>
                {visible.map((a) => (
                  <tr key={a.id} className="border-t border-white/5 hover:bg-white/[0.03]" data-testid={`application-row-${a.id}`}>
                    <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{a.created_at ? a.created_at.slice(0, 10) : "—"}</td>
                    <td className="px-4 py-3 text-white font-medium whitespace-nowrap">
                      {a.full_name}
                      {a.replied_at && (
                        <span className="ml-2 inline-flex items-center gap-1 text-[10px] font-mono uppercase text-emerald-400" data-testid={`replied-badge-${a.id}`}>
                          <MailCheck className="h-3 w-3" /> Replied
                        </span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-gray-300 whitespace-nowrap">{a.role}</td>
                    <td className="px-4 py-3 text-gray-400 whitespace-nowrap">{[a.city, a.province].filter(Boolean).join(", ")}</td>
                    <td className="px-4 py-3">
                      <div className="text-gray-300">{a.email}</div>
                      <div className="text-gray-500 text-xs">{a.phone}</div>
                    </td>
                    <td className="px-4 py-3 text-gray-400 text-xs whitespace-nowrap">{a.licensed}</td>
                    <td className="px-4 py-3 text-gray-500 text-xs max-w-[200px] truncate" title={a.message}>{a.message || "—"}</td>
                    <td className="px-4 py-3">
                      <select
                        value={a.status || "new"}
                        onChange={(e) => setStatus(a.id, e.target.value)}
                        data-testid={`status-select-${a.id}`}
                        className={`text-xs font-medium px-2 py-1.5 rounded-sm border bg-[#0D0E12] focus:outline-none ${statusColor(a.status || "new")}`}
                      >
                        {STATUSES.map((s) => (
                          <option key={s.value} value={s.value}>{s.label}</option>
                        ))}
                      </select>
                    </td>
                    <td className="px-4 py-3 whitespace-nowrap">
                      <button
                        onClick={() => { setReplyTarget(a); setReplyText(""); }}
                        data-testid={`reply-application-${a.id}`}
                        className="text-gray-400 hover:text-[#FF5500] transition-colors p-1"
                        aria-label="Reply to applicant"
                        title="Reply by email"
                      >
                        <Mail className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => remove(a.id)}
                        data-testid={`delete-application-${a.id}`}
                        className="text-gray-500 hover:text-red-400 transition-colors p-1 ml-1"
                        aria-label="Delete application"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {replyTarget && (
        <div className="fixed inset-0 z-[60] bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="reply-modal">
          <div className="w-full max-w-lg border border-white/15 bg-[#131620] rounded-sm p-6">
            <div className="flex items-start justify-between gap-4 mb-4">
              <div>
                <h2 className="font-display text-xl font-bold uppercase tracking-tight text-white">Reply to {replyTarget.full_name}</h2>
                <p className="text-xs text-gray-400 mt-1">
                  To: {replyTarget.email} · From: Maze Security Solutions (replies go to info@mazesecurity.ca)
                </p>
              </div>
              <button onClick={() => setReplyTarget(null)} data-testid="reply-modal-close" className="text-gray-400 hover:text-white p-1" aria-label="Close reply">
                <X className="h-5 w-5" />
              </button>
            </div>
            <textarea
              rows={7}
              autoFocus
              data-testid="reply-message-input"
              value={replyText}
              onChange={(e) => setReplyText(e.target.value)}
              placeholder="Write your reply — e.g. interview availability, licensing next steps, shift details…"
              className="w-full bg-[#0D0E12] border border-white/15 rounded-sm px-4 py-3 text-sm text-gray-100 placeholder-gray-500 focus:outline-none focus:border-[#FF5500] transition-colors"
            />
            <div className="flex justify-end gap-3 mt-4">
              <button
                onClick={() => setReplyTarget(null)}
                data-testid="reply-cancel-button"
                className="text-sm text-gray-400 hover:text-white px-4 py-2 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={sendReply}
                disabled={sending || !replyText.trim()}
                data-testid="reply-send-button"
                className="inline-flex items-center gap-2 bg-[#FF5500] hover:bg-[#E04800] disabled:opacity-50 text-white text-sm font-semibold px-5 py-2.5 rounded-sm transition-colors"
              >
                {sending && <Loader2 className="h-4 w-4 animate-spin" />}
                {sending ? "Sending…" : "Send Reply"}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
